import Router from 'next/router';
import uuid from 'uuid/v4';

import { Store, StorageType, EventCapsule } from '@nakedplatform/support';
import { BasicItemType } from '@nakedplatform/business';

/**
 * Optional parameters passed into the quoting flow that
 * are not to be collected during chat by the Messenger.
 */
export interface ChatInitData {
  /** Value of the BASIC item being insured */
  itemValue?: number;
  /** Type of BASIC item being insured */
  basicItemType?: BasicItemType;
  /** Building value in cents */
  buildingValue?: number;
  /** Floor area of the building in square metres — pre-fills the Rasa slot so the question is skipped */
  squareMeters?: number;
  /** Finish quality of the building */
  finishQuality?: string;
  /** Version of the building value calculator that produced the quote params (e.g. 1.0) */
  buildingValueVersion?: string;
  /** Source identifier for the building value (e.g. building-value-calculator) */
  buildingValueSource?: string;
}

/**
 * Captures and stores the referrer page when entering quote flow
 * Only stores internal referrers (same domain), ignores external sources
 */
export function captureQuoteReferrer() {
  const storage = Store.getStorage(StorageType.SessionStorage);

  if (!storage) {
    return;
  }

  // Don't overwrite existing referrer (user might refresh or navigate within quote flow)
  const existingReferrer = storage.getItem('quoteReferrer');

  if (existingReferrer && parseInternalReferrerPath(existingReferrer)) {
    return;
  }

  if (existingReferrer) {
    storage.removeItem('quoteReferrer');
  }

  const referrer = document.referrer;
  const currentOrigin = window.location.origin;
  const referrerPath = getInternalReferrerPath(referrer, currentOrigin);

  // Don't store if referrer is already a quote page (prevents loops)
  if (referrerPath && !referrerPath.includes('/quote/') && !referrerPath.includes('/summary')) {
    storage.setItem('quoteReferrer', referrerPath);
  }
}
/**
 * Gets the stored quote referrer and clears it from storage
 */
export function getAndClearQuoteReferrer(): string | null {
  const storage = Store.getStorage(StorageType.SessionStorage);

  if (!storage) {
    return null;
  }

  const referrer = storage.getItem('quoteReferrer');

  if (referrer) {
    storage.removeItem('quoteReferrer');

    return parseInternalReferrerPath(referrer);
  }

  return null;
}
/**
 * Converts an absolute same-origin referrer into an internal path.
 *
 * @param referrer - Absolute browser referrer to validate.
 * @param currentOrigin - Current trusted browser origin.
 */
function getInternalReferrerPath(referrer: string, currentOrigin: string): string | null {
  try {
    const referrerUrl = new URL(referrer);
    const originUrl = new URL(currentOrigin);

    if (referrerUrl.origin !== originUrl.origin) {
      return null;
    }

    return parseInternalReferrerPath(
      `${referrerUrl.pathname}${referrerUrl.search}${referrerUrl.hash}`
    );
  } catch {
    return null;
  }
}
/**
 * Rejects protocol-relative and external navigation values.
 *
 * @param referrer - Stored navigation value to validate.
 */
function parseInternalReferrerPath(referrer: string): string | null {
  if (!referrer.startsWith('/')) {
    return null;
  }

  try {
    const internalOrigin = 'https://quote-referrer.invalid';
    const url = new URL(referrer, internalOrigin);

    if (url.origin !== internalOrigin) {
      return null;
    }

    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
}
/**
 * Creates or fetches the chat ID from storage
 *
 * TODO: Return null if not found and handle it in the chat component.
 * The user should be given the option to restart of close the chat.
 */
export function getChatId() {
  const storage = Store.getStorage(StorageType.SessionStorage);

  if (storage) {
    const id = storage.getItem('chatId');

    if (id) {
      return id;
    }

    /**
     * NOTE!!! We should not be generating a new chatId if one does not exist.
     * Generating a new chatId if one does not exist could be fatal if the chatId (which is random) is not known by rasa.
     * This could be the case when a user restores a browser tab with an active chat session, or copies the chat url to another tab.
     **/
    const chatId = uuid();

    storage.setItem('chatId', chatId);

    return chatId;
  }

  return uuid();
}
/**
 * Removes last state from chatPath
 */
export function popChatHistory() {
  const { options } = window.history.state;

  if (options) {
    options.position = options.position - 1;
  }

  Router.back();
}
/**
 * Pushes the chat state to browser history
 *
 * @param event - The state to push onto the history
 */
export function pushChatHistory(event: EventCapsule) {
  const { stateName } = event.payload;
  const { options } = window.history.state;
  const basePath = Router.basePath;

  if (!isWeb(stateName)) {
    return;
  }

  const path = convertToKebab(stateName.replace('Web', ''));
  const { pathname, search } = window.location;
  const relativePathname = pathname.replace(`${basePath}`, '');

  // Skip if already on the correct path - Used during back-navigation to a previous question or refreshing the page
  // eg http://localhost:3000/quote/single-item?path=user-details
  if (pathname.startsWith(`${basePath}/quote/`) && search === `?path=${path}`) {
    return;
  }

  // Rewrite the url to include the path parameter, and only push the rewritten url
  // Used when entering the chat normally via the Get-My-Price button
  // eg http://localhost:3000/quote/single-item => http://localhost:3000/quote/single-item?path=user-details
  if (pathname.startsWith(`${basePath}/quote/`) && !search) {
    const transition = {
      position: options.position,
    } as any;

    void Router.replace(relativePathname, `${relativePathname}?path=${path}`, transition);
  }
  // Rewrite the url to include the path parameter, and only push the rewritten url
  // Required when url has prefill and utm params
  // eg http://localhost:3000/quote/single-item?type=SPORTS&utm_source=google => http://localhost:3000/quote/single-item?path=user-details
  else if (pathname.startsWith(`${basePath}/quote/`) && search && !search.includes('path=')) {
    const transition = {
      position: (options.position as number) + 1,
    } as any;

    void Router.replace(relativePathname, `${relativePathname}?path=${path}`, transition);
  }
  // Pushes the url to the history stack, used when navigating to the next question
  else {
    const transition = {
      position: (options.position as number) + 1,
    } as any;

    void Router.push(relativePathname, `${relativePathname}?path=${path}`, transition);
  }
}
/**
 * Converts a string to kebab case
 *
 * @param input - String to convert
 */
function convertToKebab(input: string) {
  return input.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
}
/**
 * Indicates if the given state is a Web state
 *
 * @param stateName - Name of the current state
 */
function isWeb(stateName: string) {
  return stateName.includes('Web');
}
